import React, { useEffect, useRef, useState, useCallback } from 'react';
import { VideoPlayer } from '@components';
import UnmutedIcon from './unmute.svg?react';
import MuteIcon from './mute.svg?react';
import ShareIcon from 'src/assets/icons/new-share.svg?react';
import Button from '@src/components/button/Button';
import styles from '../UnitProgressModal.module.scss';
import { IconClock, IconStandalone } from '@src/constants';
import { LordIcon } from '@src/components/lord-icon/lord-icon';

type Props = {
  metaData?: {};
  handleGoLessons?: () => void
  title?: string;
  subTitle?: string;
  src?: string | null;
  playing: boolean
  onPlayPreviewVideo?: () => void;
  assessments?: boolean;
  viewLabel?: string;
  unitUniqueName?: string | null;
  isNewCover?: boolean;
  suggestedDuration?: string;
  suggestedLessonCount?: string;
  handleUnitShareModal?: () => void;
  forceVideoMute?: boolean; // New prop to force video mute state
};

const CoverVideoPlayer = React.forwardRef((props: Props, ref: React.ForwardedRef<{setMute: (muted: boolean) => void}>) => {
  const { handleGoLessons, title, subTitle, src, metaData, playing, onPlayPreviewVideo, assessments, viewLabel='', unitUniqueName, suggestedLessonCount, suggestedDuration, isNewCover, handleUnitShareModal, forceVideoMute } = props;

  const [muted, setMuted] = useState(true);
  const [replay, setReplay] = useState(false);
  const [firstPlay, setFirstPlay] = useState(false);
  const [muteCount, setMuteCount] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const muteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setReplay(playing)
  }, [playing]);

  // Handle forced mute state
  useEffect(() => {
    if (forceVideoMute) {
      setMuted(true);
    }
  }, [forceVideoMute]);

  useEffect(() => {
    let timeout: any;
  
    const displayOpa = () => {
      if (muteRef.current) {
        muteRef.current.style.opacity = "1"
      }
      clearTimeout(timeout);

      timeout = setTimeout(() => {
        if (muteRef.current) {
          muteRef.current.style.opacity = "0"
        }
      }, 2000);
    };
    let refWrapper: any = null;
    if (wrapperRef.current) {
      refWrapper = wrapperRef.current
      refWrapper.addEventListener('mousemove', displayOpa);
    }

    return () => {
      if (refWrapper) {
        refWrapper.removeEventListener('mousemove', displayOpa);
      }
    };
  
  }, [wrapperRef, muteRef]);

  const handleVideo = () => {
    setMuted(current => !current)
    setMuteCount(current => current + 1);
    if (muteCount === 0) {
      setFirstPlay(true);
      onPlayPreviewVideo?.();
    } else {
      setFirstPlay(false);
    }
  }

  const handleEnd = useCallback(() => {
    setMuted(true);
  },[setMuted])

  const onToggleLesson = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setMuted(true); // Mute video when lesson is toggled
    handleGoLessons?.();
  }, [handleGoLessons]);

  const handleShareUnit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMuted(true); // Mute video when sharing
    handleUnitShareModal?.();
  }

  const suggestBar = (
    <div className={styles.unitSuggestView}>
      <div className={styles.lessonsView}>
        <LordIcon src={IconStandalone} isHover={true} colors={{ primary: "#FFF", secondary: "#FFF" }} size={40}/>
        <div className={styles.lessonsText}>{suggestedLessonCount} Lessons</div>
      </div>
      {suggestedDuration ? <div className={styles.durationView}>
        <LordIcon src={IconClock} isHover={true} colors={{ primary: "#33C0AF", secondary: "#33C0AF" }} size={20}/>
        <div className={styles.durationText}>{`${suggestedLessonCount}`}&nbsp;<span>x</span>&nbsp;{`${suggestedDuration}`}&nbsp;<span>min</span></div>
      </div> : null}
    </div>
  )

  // Expose setMuted function through ref
  React.useImperativeHandle(ref, () => ({
    setMute: (muted: boolean) => {
      setMuted(muted);
    }
  }));

  return (
    <div ref={wrapperRef} className={styles.videoView} onClick={handleVideo}>
      <VideoPlayer isCoverVideo src={src} metaData={metaData || {video_title: 'Cover Video'}} autoPlay={false} muted={muted} className={styles.video} loop={false} controls={false} replay={replay} onNext={handleEnd} firstPlay={firstPlay}/>
      { muted && <>
        <div className={styles.gradientView}></div>
        <div className={styles.unitTitle}>
          <div className={styles.textView}>
            <h2>{title}</h2>
            {!assessments && <p>{subTitle}</p>}
          </div>
          {(viewLabel !== 'View Lessons') && <Button size='small' label={viewLabel} className={styles.lessonViweButton} onClick={onToggleLesson} />}
        </div>
      </>}
      {muted
        ? <div className={styles.unmuteIcon} style={{opacity: muted ? '1': 'none'}}><UnmutedIcon /></div>
        : <div ref={muteRef} className={styles.muteIcon}><MuteIcon /></div>
      }
      {unitUniqueName && <div className={styles.shareIcon} onClick={handleShareUnit}>
        <Button label='Share Unit' size='small' className={styles.bannerShareButton} leftIcon={<ShareIcon width={24} height={24}/>}/>
      </div>}
      {isNewCover ? suggestBar : null}
    </div>
  );
});

export default CoverVideoPlayer;
